import type {
  Category,
  Diagnostics,
  Entry,
  ImageType,
  LocaleCode,
} from "../shared/types";

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body = "";
    try {
      body = await res.text();
    } catch {
      // ignore
    }
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function noContent(res: Response): Promise<void> {
  if (!res.ok) {
    let body = "";
    try {
      body = await res.text();
    } catch {
      // ignore
    }
    throw new Error(`${res.status}: ${body}`);
  }
}

export interface LocalesResponse {
  default: LocaleCode;
  locales: LocaleCode[];
  publicOrigin: string;
}

export interface SettingsResponse {
  aiModel: string | null;
  effectiveModel: string;
  envModel: string | null;
  default: string;
}

export type BulkEvent =
  | {
      type: "progress";
      locale: LocaleCode;
      done: number;
      total: number;
      savedThisChunk: number;
    }
  | {
      type: "chunk_failed";
      locale: LocaleCode;
      chunkIndex: number;
      reason: string;
    }
  | {
      type: "done";
      saved: number;
      failed: number;
      failedChunks: { locale: LocaleCode; index: number }[];
    };

export const api = {
  health(): Promise<{ status: string }> {
    return fetch("/api/health", { credentials: "include" }).then((r) =>
      jsonOrThrow(r)
    );
  },

  me(): Promise<{ authenticated: boolean }> {
    return fetch("/api/me", { credentials: "include" }).then((r) =>
      jsonOrThrow(r)
    );
  },
  login(username: string, password: string): Promise<{ ok: true }> {
    return fetch("/api/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }).then((r) => jsonOrThrow<{ ok: true }>(r));
  },
  logout(): Promise<{ ok: true }> {
    return fetch("/api/logout", {
      method: "POST",
      credentials: "include",
    }).then((r) => jsonOrThrow<{ ok: true }>(r));
  },

  getLocales(): Promise<LocalesResponse> {
    return fetch("/api/locales", { credentials: "include" }).then((r) =>
      jsonOrThrow<LocalesResponse>(r)
    );
  },
  addLocale(code: LocaleCode): Promise<LocalesResponse> {
    return fetch("/api/locales", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    }).then((r) => jsonOrThrow<LocalesResponse>(r));
  },
  removeLocale(code: LocaleCode): Promise<void> {
    return fetch(`/api/locales/${encodeURIComponent(code)}`, {
      method: "DELETE",
      credentials: "include",
    }).then(noContent);
  },

  listEntries(): Promise<Entry[]> {
    return fetch("/api/entries", { credentials: "include" }).then((r) =>
      jsonOrThrow<Entry[]>(r)
    );
  },
  getEntry(id: string): Promise<Entry> {
    return fetch(`/api/entries/${encodeURIComponent(id)}`, {
      credentials: "include",
    }).then((r) => jsonOrThrow<Entry>(r));
  },
  createEntry(form: FormData): Promise<Entry> {
    return fetch("/api/entries", {
      method: "POST",
      credentials: "include",
      body: form,
    }).then((r) => jsonOrThrow<Entry>(r));
  },
  updateEntry(id: string, form: FormData): Promise<Entry> {
    return fetch(`/api/entries/${encodeURIComponent(id)}`, {
      method: "PATCH",
      credentials: "include",
      body: form,
    }).then((r) => jsonOrThrow<Entry>(r));
  },
  deleteEntry(id: string): Promise<void> {
    return fetch(`/api/entries/${encodeURIComponent(id)}`, {
      method: "DELETE",
      credentials: "include",
    }).then(noContent);
  },

  putTranslations(
    id: string,
    body: { titles?: Record<LocaleCode, string>; capitals?: Record<LocaleCode, string> }
  ): Promise<Entry> {
    return fetch(`/api/entries/${encodeURIComponent(id)}/translations`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<Entry>(r));
  },

  listCategories(): Promise<Category[]> {
    return fetch("/api/categories", { credentials: "include" }).then((r) =>
      jsonOrThrow<Category[]>(r)
    );
  },
  putCategoryMembers(catId: string, countries: string[]): Promise<Category> {
    return fetch(
      `/api/categories/${encodeURIComponent(catId)}/countries`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countries }),
      }
    ).then((r) => jsonOrThrow<Category>(r));
  },

  getDiagnostics(): Promise<Diagnostics> {
    return fetch("/api/diagnostics", { credentials: "include" }).then((r) =>
      jsonOrThrow<Diagnostics>(r)
    );
  },

  deleteImage(type: ImageType, filename: string): Promise<void> {
    return fetch(
      `/api/images/${encodeURIComponent(type)}/${encodeURIComponent(filename)}`,
      { method: "DELETE", credentials: "include" }
    ).then(noContent);
  },

  reserialize(): Promise<{ regenerated: number }> {
    return fetch("/api/admin/reserialize", {
      method: "POST",
      credentials: "include",
    }).then((r) => jsonOrThrow(r));
  },

  aiStatus(): Promise<{ enabled: boolean; model?: string }> {
    return fetch("/api/ai/status", { credentials: "include" }).then((r) =>
      jsonOrThrow(r)
    );
  },
  getSettings(): Promise<SettingsResponse> {
    return fetch("/api/settings", { credentials: "include" }).then((r) =>
      jsonOrThrow<SettingsResponse>(r)
    );
  },
  putSettings(aiModel: string | null): Promise<SettingsResponse> {
    return fetch("/api/settings", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aiModel }),
    }).then((r) => jsonOrThrow<SettingsResponse>(r));
  },
  aiModels(): Promise<{ models: { id: string; name: string }[] }> {
    return fetch("/api/ai/models", { credentials: "include" }).then((r) =>
      jsonOrThrow(r)
    );
  },
  aiTest(model?: string): Promise<{ ok: boolean; error?: string }> {
    return fetch("/api/ai/test", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(model ? { model } : {}),
    }).then((r) => jsonOrThrow(r));
  },
  aiTranslateRow(
    id: string,
    locales: LocaleCode[]
  ): Promise<{
    entry: Entry;
    saved: { locale: LocaleCode; fields: ("title" | "capital")[] }[];
    failed: { locale: LocaleCode; reason: string }[];
  }> {
    return fetch("/api/ai/translate-row", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, locales }),
    }).then((r) => jsonOrThrow(r));
  },
  aiTranslateBulk(
    ids: string[],
    locales: LocaleCode[],
    onEvent: (e: BulkEvent) => void,
    signal?: AbortSignal
  ): Promise<void> {
    return fetch("/api/ai/translate-bulk", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, locales }),
      signal,
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${body}`);
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const block = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          let event = "message";
          let data = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) data = line.slice(6);
          }
          if (!data) continue;
          try {
            const payload = JSON.parse(data);
            onEvent({ type: event, ...payload } as BulkEvent);
          } catch {
            // ignore malformed blocks
          }
        }
      }
    });
  },
};
